import { useCallback, useMemo, useState } from 'react';

import { readSelectedBatch, writeSelectedBatch } from '../lib/selected-batch-store';

/**
 * Which batch the attendance board is pointed at.
 *
 * Resolution order — first valid wins:
 *   1. what the user clicked in this session
 *   2. what was last used on this device for this institute (localStorage)
 *   3. the first batch the server returned
 *
 * "Valid" always means "present in `batchIds`", so a remembered batch that was
 * deleted, renamed away, or belongs to another institute silently degrades to
 * the first batch rather than leaving the board pointed at nothing.
 *
 * The persisted value is read during render rather than seeded through an
 * effect: on a hard refresh the correct batch is selected on the very first
 * paint, with no flash of batch #1. Nothing else writes these keys, so there is
 * no external store to subscribe to.
 *
 * `extraIds` widens what counts as valid without widening the fallback — the
 * board's "All batches" scope is a real, persistable selection but must never
 * become the default for someone who has never picked it.
 *
 * `batchIds` and `extraIds` must be referentially stable (memoize them at the
 * call site) — both are dependencies of the derivation.
 */
const NO_EXTRA_IDS: readonly string[] = [];

export function useSelectedBatch(
  instituteId: string | null,
  batchIds: readonly string[],
  extraIds: readonly string[] = NO_EXTRA_IDS,
): { selectedBatchId: string | null; selectBatch: (batchId: string) => void } {
  const stored = useMemo(
    () => (instituteId !== null ? readSelectedBatch(instituteId) : null),
    [instituteId],
  );

  const [chosen, setChosen] = useState<string | null>(null);

  const selectedBatchId = useMemo(() => {
    const isValid = (id: string | null): id is string =>
      id !== null && (batchIds.includes(id) || extraIds.includes(id));
    if (isValid(chosen)) return chosen;
    if (isValid(stored)) return stored;
    return batchIds[0] ?? null;
  }, [chosen, stored, batchIds, extraIds]);

  const selectBatch = useCallback(
    (batchId: string): void => {
      setChosen(batchId);
      if (instituteId !== null) {
        writeSelectedBatch(instituteId, batchId);
      }
    },
    [instituteId],
  );

  return { selectedBatchId, selectBatch };
}
